import {
  AnalyticsEventName,
  DUPE_COMPARE_TO_COOKIE_KEY,
  FeatureFlags,
  UploadFileTypes,
} from '@dupe/common/constants'
import { getShopKey } from '@dupe/common/helpers'
import { CameraIcon } from '@heroicons/react/24/solid'
import { BroadcastChannel } from 'broadcast-channel'
import { deleteCookie, setCookie } from 'cookies-next'
import { motion } from 'framer-motion'
import { ArrowRightIcon, ArrowUpIcon, XIcon } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useState, useRef, useEffect, useCallback } from 'react'
import { type Control, useFormContext } from 'react-hook-form'
import DupeInputProOptions from '~/src/components/DupeInputProOptions'
import InputImageUpload from '~/src/components/InputImageUpload'
import AnimatedBlurContainer from '~/src/components/assistant/AnimatedBlurContainer'
import CameraUploadIcon from '~/src/components/svg/CameraUploadIcon'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { SearchDataInput, useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import useInIframe from '~/src/hooks/useInIframe'
import getSearchUrl from '~/src/lib/getSearchUrl'
import { extractUrl, isDupeUrl, isValidUrl } from '~/src/lib/helpers'
import { cn } from '~/src/lib/utils'
import useGetExperimentActive from '~/src/hooks/useGetExperimentActive'
import { useCustomBackgroundRoutes } from '~/src/hooks/useCustomBackgroundRoutes'

interface Props {
  children?: ({
    control,
    isDirty,
    hasValue,
    isUrl,
  }: {
    control: Control<any>
    isDirty: boolean | undefined
    hasValue: boolean
    isUrl: boolean
  }) => React.ReactNode
  unstyled?: boolean
  source?: string
  allowCamera?: boolean
  ignoreBroadcast?: boolean
  autoFocus?: boolean
  openInNewTab?: boolean
  className?: string
  enableGradient?: boolean
  expanded?: boolean
  bgTransparent?: boolean
  inputClassName?: string
  placeholder?: string
}

const DupeInput: React.FC<Props> = ({
  children,
  unstyled,
  source,
  allowCamera,
  ignoreBroadcast,
  autoFocus,
  openInNewTab,
  className,
  enableGradient,
  expanded,
  bgTransparent,
  inputClassName,
  placeholder = 'Paste URL or search',
  ...props
}) => {
  const router = useRouter()
  const analytics = useAnalytics()
  const [shouldPulse, setShouldPulse] = useState(true)

  // Image preview state
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null)
  const [uploadedImageHash, setUploadedImageHash] = useState<string | null>(
    null
  )

  const { isMobile } = useDeviceDetect()

  // Check if we're on a page with custom background color (rewards, blog, etc.)
  const hasCustomBackground = useCustomBackgroundRoutes()

  const {
    register,
    control,
    handleSubmit,
    setFocus,
    setValue,
    formState,
    watch,
  } = useFormContext<SearchDataInput>()

  const { inIframe } = useInIframe()

  const {
    setSearchState,
    searchState,
    isDragActive,
    uploadedFileResult,
    processing,
    setHasInteracted,
    setUploadClickType,
    setSourceType,
    handleImageUpload,
  } = useSearchContext()

  const qValue = watch('q')
  const isDirty = formState.dirtyFields.q || !!qValue || !!searchState.query
  const hasValue = !!qValue || !!searchState.query
  const isUrl = isValidUrl(qValue) instanceof URL

  const { active: showDupeAIChat } = useGetExperimentActive(
    FeatureFlags.DupeAIChat
  )
  const { active: showDupeAIHomepage } = useGetExperimentActive(
    FeatureFlags.DupeAIHomepage
  )

  // Helper function to process images for preview
  const processImageForPreview = useCallback(
    async (file: File, source: 'paste' | 'upload') => {
      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          console.warn('File is not an image:', file.type)
          return false
        }

        // Validate file size (20MB limit)
        const MAX_FILE_SIZE = 20000000
        if (file.size > MAX_FILE_SIZE) {
          alert('Image size must be less than 20MB')
          return false
        }

        // Set upload type and source for tracking
        setUploadClickType(UploadFileTypes.IMAGE)
        setSourceType(
          source === 'paste' ? 'dupe-input-paste' : 'dupe-input-upload'
        )

        // Track analytics
        analytics.track(AnalyticsEventName.ClickedInputMedia, {
          media: source,
        })

        // Read file as data URL for preview
        const reader = new FileReader()
        reader.onload = async (e) => {
          if (e.target?.result) {
            const fileData = e.target.result as string
            setImagePreview(fileData)

            // Start uploading the image to server
            try {
              const { hash, imageUrl } = await handleImageUpload(fileData)
              setUploadedImageUrl(imageUrl)
              setUploadedImageHash(hash)
            } catch (error) {
              console.error('Error uploading image:', error)
              // Don't show alert here as the user can still submit manually
            }
          }
        }
        reader.readAsDataURL(file)

        return true
      } catch (error) {
        console.error('Error processing image:', error)
        return false
      }
    },
    [setUploadClickType, setSourceType, analytics, handleImageUpload]
  )

  const inputContainerRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const { ref: textareaRegisterRef, ...textareaProps } = register('q', {
    validate: (value) => {
      if (!value) return true
      const isUrl = isValidUrl(value) instanceof URL
      return isUrl ? !!isValidUrl(value) || 'Not a valid product URL' : true
    },
  })

  const onSubmit = useCallback(
    (method = 'submit') =>
      async (data: SearchDataInput) => {
        const searchQuery = data.q || ''

        // If there is no query and no image, return
        if (!searchQuery && !imagePreview) return

        // Handle image submission (with or without text)
        if (imagePreview) {
          // If we already have the uploaded image hash, redirect directly
          if (uploadedImageHash && uploadedImageUrl) {
            setSearchState({
              searching: true,
              source,
              method,
            })

            // Navigate to results page with the hash, including text query if present
            let redirectUrl = `/results/${uploadedImageHash}?url=${encodeURIComponent(uploadedImageUrl)}`
            if (showDupeAIHomepage) {
              redirectUrl = `/chats/${uploadedImageHash}?url=${encodeURIComponent(uploadedImageUrl)}`
            }

            if (searchQuery) {
              redirectUrl += `&q=${encodeURIComponent(searchQuery)}`
            }

            if (openInNewTab) {
              window.open(redirectUrl)
            } else {
              router.push(redirectUrl)
            }

            // Clear the preview after submission
            setImagePreview(null)
            setUploadedImageUrl(null)
            setUploadedImageHash(null)
            return
          }

          // If image hasn't been uploaded yet, show error
          alert('Image is still uploading. Please wait a moment and try again.')
          return
        }

        // This is important to trigger our broadcast & open a home page tab on search
        // We need to also ensure we don't set the cookie if we are ignoring the broadcast
        if (data.compare && !ignoreBroadcast) {
          setCookie(DUPE_COMPARE_TO_COOKIE_KEY, data.compare)
        } else {
          deleteCookie(DUPE_COMPARE_TO_COOKIE_KEY)
        }

        // Reddit Pixel - track "Search"
        if (window.rdt) {
          window.rdt('track', 'Search')
        }

        // Facebook Pixel - track "Search"
        if (window.fbq) {
          window.fbq('track', 'Search')
        }

        const validUrl = isValidUrl(searchQuery)
        const extractedUrl = extractUrl(searchQuery)
        const urlToDupe = validUrl || extractedUrl

        // if it's a valid URL, dupe it
        if (
          urlToDupe &&
          urlToDupe instanceof URL &&
          urlToDupe.pathname !== '/'
        ) {
          const dupeUrl = isDupeUrl(searchQuery, window.location.hostname)

          analytics.track(
            AnalyticsEventName.PerformedDupe,
            {
              ...data,
              source,
              method,
              is_dupe_url: dupeUrl,
              compare: data.compare,
              shopKey: getShopKey(urlToDupe.toString()),
            },
            {
              sendToPlausible: true,
              sendToTinybird: true,
              sendToFb: true,
              sendToPodscribe: true,
            }
          )

          // Redirect to the original URL if it's a dupe URL
          if (dupeUrl) {
            window.open(searchQuery)
          } else {
            const searchParams = new URLSearchParams()
            searchParams.set('url', urlToDupe.toString())

            // Open as a relative path to ensure it uses the current origin,
            // which will match Playwright's BASE_URL in test environments.
            const relativeSearchPath = `/?url=${encodeURIComponent(
              urlToDupe.toString()
            )}`

            if (openInNewTab) {
              window.open(relativeSearchPath)
            } else {
              router.push(relativeSearchPath)
            }
          }

          return
        }

        analytics.track(AnalyticsEventName.SearchTermEntered, {
          query: data.q,
          source,
          method,
          aichatEnabled: showDupeAIChat,
        })

        const searchUrl = showDupeAIChat
          ? `/chats/prompt?q=${encodeURIComponent(searchQuery)}`
          : `/search?q=${encodeURIComponent(searchQuery)}`

        // otherwise, perform a search
        if (!openInNewTab) {
          setSearchState({
            query: searchQuery,
            searching: true,
            refresh: true,
            source,
            method,
          })
          // set query on current url using router
          router.replace(searchUrl)
        } else {
          window.open(searchUrl)

          // This is important to trigger our broadcast & open a home page tab on search
          // however not if we are ignoring the broadcast e.g DupeInput on results page (ignoreBroadcast = true)
          if (data.compare && !ignoreBroadcast) {
            const shopKey = isMobile ? 'walmart' : 'wayfair'
            const searchUrl = getSearchUrl({
              query: encodeURIComponent(searchQuery),
              source: 'search',
              shopKey,
            })

            // Also search message listener to redirect the current page
            if (!!searchUrl) {
              window.location.assign(searchUrl)
            }
          }
        }
      },
    [
      analytics,
      ignoreBroadcast,
      imagePreview,
      isMobile,
      openInNewTab,
      router,
      setSearchState,
      showDupeAIChat,
      showDupeAIHomepage,
      source,
      uploadedImageHash,
      uploadedImageUrl,
    ]
  )

  const triggerSubmit = (method = 'button') => handleSubmit(onSubmit(method))()

  const onInputClick = () => {
    setHasInteracted(true)

    const timer = setTimeout(() => {
      inputContainerRef.current?.focus()
    }, 100)

    return () => clearTimeout(timer)
  }

  const renderTrigger = ({
    hideLabel = false,
    expanded = true,
  }: {
    hideLabel?: boolean
    expanded?: boolean
  } = {}) => {
    return (
      <>
        <div
          className={cn(
            'flex flex-row items-center text-black/50 transition-colors hover:text-black/100',
            {
              'dark:text-current/50 dark:hover:text-current/100': !expanded,
            }
          )}
        >
          <CameraIcon className='h-[22px] w-[22px] opacity-70 md:hidden' />
          <CameraUploadIcon className='hidden h-[20px] w-[20px] md:ml-1 md:inline-block' />
        </div>

        {!hideLabel && (
          <span className='hidden gap-1 text-sm font-medium text-black/70 transition-opacity group-hover:text-black/100 md:inline-flex'>
            <span>Image</span>
          </span>
        )}
      </>
    )
  }

  // listen for broadcast messages
  useEffect(() => {
    if (ignoreBroadcast) return
    if (inIframe) return

    const channel = new BroadcastChannel<{
      dupeCompareTo: string
      productTitle: string
    }>('dupe.com')

    channel.onmessage = (msg) => {
      const { dupeCompareTo, productTitle } = msg || {}
      const shopKey = isMobile ? 'walmart' : 'wayfair'

      const searchUrl = getSearchUrl({
        query: productTitle,
        source,
        shopKey,
      })

      // Also search message listener to redirect the current page
      if (productTitle && !!dupeCompareTo && !!searchUrl) {
        window.location.assign(searchUrl)
      }
    }

    return () => {
      channel.close()
    }
  }, [inIframe, ignoreBroadcast, source, isMobile])

  // autofocus on input
  useEffect(() => {
    if (autoFocus) {
      setFocus('q')
    }
  }, [autoFocus, setFocus])

  // set value from prop
  useEffect(() => {
    if (searchState.query) {
      setValue('q', searchState.query)
    }
  }, [setValue, searchState.query])

  // Add useEffect to handle pulse animation timing
  useEffect(() => {
    // Animation delay is 1500ms + duration 3000ms = 4500ms total time
    const animationTotalTime = 4500

    if (enableGradient && shouldPulse) {
      // Set a timeout to mark the animation as completed
      const timer = setTimeout(() => {
        setShouldPulse(false)
      }, animationTotalTime)

      return () => clearTimeout(timer)
    }
  }, [enableGradient, shouldPulse])

  return (
    <div ref={inputContainerRef} className={cn(className)}>
      <form onSubmit={handleSubmit(onSubmit())} className='relative'>
        <AnimatedBlurContainer
          borderClassName={cn({
            'rounded-full': !expanded,
            'rounded-[24px]': expanded,
            hidden: bgTransparent,
          })}
          className={cn(
            'flex flex-col bg-white px-3 text-black md:px-4',
            inputClassName,
            {
              'dark:border-current/50 border border-black/10':
                !unstyled && !hasCustomBackground,
              // 50% darker border if custom background
              'dark:border-current/20 border border-black/20':
                hasCustomBackground,
              'rounded-full py-1.5 md:pr-2': !expanded,
              'min-h-[140px] rounded-3xl pt-3 shadow-2xl md:min-h-[154px]':
                expanded,
            }
          )}
          childrenClassName='flex flex-col flex-1'
          borderWidth={enableGradient && shouldPulse ? 3 : 0}
          blur={enableGradient && shouldPulse ? 'xs' : 'none'}
          glow={enableGradient && shouldPulse}
          pulseOnce={enableGradient && shouldPulse}
        >
          {/* Image preview above textarea */}
          {imagePreview && expanded && (
            <div className='mb-2 ml-1 mt-1 flex'>
              <button
                className='relative'
                type='button'
                onClick={() => {
                  setImagePreview(null)
                  setUploadedImageUrl(null)
                  setUploadedImageHash(null)
                }}
              >
                <Image
                  src={imagePreview}
                  alt='Uploaded image'
                  width={60}
                  height={60}
                  className='aspect-square rounded-xl object-cover'
                  unoptimized
                />
                {processing && (
                  <div className='absolute inset-0 flex items-center justify-center rounded-xl bg-black/50'>
                    <div className='animate-spin'>
                      <LoaderIcon className='h-6 w-6 text-white' />
                    </div>
                  </div>
                )}
                {!processing && (
                  <div className='absolute right-1 top-1 rounded-full bg-black/80 p-0.5 text-white transition-colors hover:bg-black'>
                    <XIcon className='h-3.5 w-3.5' />
                  </div>
                )}
              </button>
            </div>
          )}

          <label
            className={cn('flex w-full items-center', {
              'border-transparent': enableGradient,
              'border-red-700': formState.errors.q,
            })}
          >
            {formState.errors.q && (
              <div className='pointer-events-none absolute bottom-0 right-1.5 top-0 flex items-center justify-center'>
                <div className='rounded-full bg-red-700 px-2 py-1 text-sm font-semibold text-white'>
                  {formState.errors.q.message}
                </div>
              </div>
            )}

            <div className='inline-flex flex-1 overflow-hidden'>
              <div
                className={cn('relative inline-flex flex-1 transition-all', {
                  'flex h-7 flex-col justify-center md:h-10': !expanded,
                  'ml-1 mt-1 md:mt-0': expanded,
                })}
              >
                <textarea
                  ref={(e) => {
                    textareaRegisterRef(e)
                    textareaRef.current = e
                  }}
                  data-testid='dupe-input'
                  autoCapitalize='none'
                  autoComplete='off'
                  autoCorrect='off'
                  rows={expanded ? 3 : 1}
                  disabled={
                    !!uploadedFileResult || (processing && !imagePreview)
                  }
                  className={cn(
                    'block w-full resize-none border-none !bg-transparent text-black/80 placeholder:text-current md:text-lg dark:text-inherit',
                    {
                      'min-h-[72px] md:min-h-[84px]': expanded,
                      'placeholder:opacity-25': !hasCustomBackground,
                      'placeholder:opacity-50': hasCustomBackground,
                    }
                  )}
                  placeholder={
                    isDragActive
                      ? 'Drop image here'
                      : !!uploadedFileResult || processing
                        ? 'Processing...'
                        : placeholder
                  }
                  onPaste={async (
                    e: React.ClipboardEvent<HTMLTextAreaElement>
                  ) => {
                    setHasInteracted(true)

                    // Check for images in clipboard first
                    const items = Array.from(e.clipboardData.items)
                    const imageItems = items.filter((item) =>
                      item.type.startsWith('image/')
                    )

                    if (imageItems.length > 0) {
                      e.preventDefault()

                      // Process the first image found
                      const imageItem = imageItems[0]
                      const file = imageItem?.getAsFile()

                      if (file) {
                        const success = await processImageForPreview(
                          file,
                          'paste'
                        )

                        if (success) {
                          return
                        }
                      }
                    }

                    // Fallback to existing text/URL paste logic
                    setTimeout(() => {
                      const event = e.target as HTMLTextAreaElement
                      const value = event.value
                      const validUrl = isValidUrl(value)
                      const extractedUrl = extractUrl(value)
                      const urlToDupe = validUrl || extractedUrl

                      if (
                        urlToDupe &&
                        urlToDupe instanceof URL &&
                        urlToDupe.pathname !== '/'
                      ) {
                        event.blur()
                        triggerSubmit('paste')
                      }
                    }, 0)
                  }}
                  {...props}
                  onClick={onInputClick}
                  onKeyDown={(e) => {
                    setHasInteracted(true)

                    // Cmd+Enter / Ctrl+Enter → insert newline; plain Enter → submit
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      const textarea = e.target as HTMLTextAreaElement
                      const start = textarea.selectionStart
                      const end = textarea.selectionEnd
                      const currentValue = textarea.value
                      const newValue =
                        currentValue.substring(0, start) +
                        '\n' +
                        currentValue.substring(end)

                      // Keep form state in sync (context7: useFormContext)
                      setValue('q', newValue, { shouldDirty: true })

                      // Restore caret after React updates the value
                      requestAnimationFrame(() => {
                        if (textareaRef.current) {
                          textareaRef.current.selectionStart =
                            textareaRef.current.selectionEnd = start + 1
                        }
                      })
                      return
                    } else if (e.key === 'Enter') {
                      e.preventDefault()
                      triggerSubmit('enter')
                    }
                  }}
                  {...textareaProps}
                />
              </div>

              {!expanded && allowCamera && !qValue && (
                <div className='flex items-center justify-center space-x-2 px-2'>
                  {!!uploadedFileResult && (
                    <div className='flex items-center justify-center'>
                      <Image
                        src={uploadedFileResult}
                        unoptimized
                        alt=''
                        width={50}
                        height={50}
                        className='rounded object-cover'
                      />
                    </div>
                  )}

                  <InputImageUpload
                    onFileSelect={(file) =>
                      processImageForPreview(file, 'upload')
                    }
                  >
                    <button>
                      {renderTrigger({ hideLabel: true, expanded: false })}
                    </button>
                  </InputImageUpload>
                </div>
              )}
            </div>
          </label>

          {expanded ? (
            <div className='mb-3 mt-auto flex items-center gap-1.5 md:mb-4'>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className='flex items-center gap-1.5'
              >
                <InputImageUpload
                  onFileSelect={(file) =>
                    processImageForPreview(file, 'upload')
                  }
                >
                  <button className='group flex aspect-square h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white transition-colors md:aspect-auto md:h-10 md:w-auto md:gap-2 md:px-3 md:pl-2 md:hover:bg-gray-50'>
                    {renderTrigger()}
                  </button>
                </InputImageUpload>
                <div className='flex h-9 items-center rounded-full border border-black/10 bg-white pl-2 pr-4 transition-colors md:h-10 md:hover:bg-gray-50'>
                  <DupeInputProOptions source='homepage' />
                </div>
              </motion.div>

              <button
                type='submit'
                className={cn(
                  'bg-promo ml-auto flex items-center justify-center rounded-full text-white',
                  'aspect-square',
                  'h-9 md:h-10',
                  {
                    'bg-black/20': !qValue && !imagePreview,
                  }
                )}
              >
                <ArrowUpIcon className='h-6 w-6 text-white' strokeWidth={2} />
              </button>
            </div>
          ) : (
            <button
              type='submit'
              className={cn(
                'bg-promo absolute right-0 flex aspect-square h-7 items-center justify-center rounded-full text-white md:h-10',
                {
                  'opacity-0': !qValue,
                  'opacity-100': !!qValue,
                }
              )}
            >
              <ArrowRightIcon
                className='h-4 w-4 text-white md:h-7 md:w-7'
                strokeWidth={2}
              />
            </button>
          )}
        </AnimatedBlurContainer>

        {children &&
          children({
            control,
            isDirty,
            hasValue,
            isUrl,
          })}
      </form>
    </div>
  )
}

export default DupeInput
